import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  TrendingUp,
  FileSpreadsheet,
  Upload,
  Search,
  Calendar,
  User,
  Building,
  DollarSign,
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
  Eye,
  Trash2,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Layers,
  ChevronRight,
  X,
  Lock,
  Unlock,
  ShieldCheck,
  ShieldAlert,
} from 'lucide-react';
import { apiRequest } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { SalesInvoice, SalesInvoiceItem, InvoiceAnalytics, ProductAlias, Product, PeriodLock } from '../types';

export const SalesProfitability: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const canViewCost = user?.permissions.includes('pricing.view_cost') ?? false;

  const initialTab = (searchParams.get('tab') as any) || 'dashboard';
  // Active view tab
  const [activeTab, setActiveTab] = useState<'dashboard' | 'invoices' | 'import' | 'aliases' | 'periods'>(
    initialTab === 'import' || initialTab === 'aliases' || initialTab === 'periods' || initialTab === 'invoices' ? initialTab : 'dashboard'
  );

  // Cost calculation mode: 'net_cost' (default) vs 'raw_cost'
  const [costMode, setCostMode] = useState<'net_cost' | 'raw_cost'>('net_cost');

  // Filters
  const [salesPersonFilter, setSalesPersonFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [marginFilter, setMarginFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [includeCancelledInDashboard, setIncludeCancelledInDashboard] = useState(false);

  // Analytics State
  const [analytics, setAnalytics] = useState<InvoiceAnalytics | null>(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);

  // Invoices List State
  const [invoices, setInvoices] = useState<SalesInvoice[]>([]);
  const [invoicesTotal, setInvoicesTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  // Selected Invoice Detail Modal
  const [selectedInvoice, setSelectedInvoice] = useState<SalesInvoice | null>(null);
  const [selectedItems, setSelectedItems] = useState<SalesInvoiceItem[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Aliases State
  const [aliases, setAliases] = useState<ProductAlias[]>([]);
  const [loadingAliases, setLoadingAliases] = useState(false);

  // Period Locks State
  const [periods, setPeriods] = useState<PeriodLock[]>([]);
  const [loadingPeriods, setLoadingPeriods] = useState(false);
  const [adminOverride, setAdminOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [togglingPeriod, setTogglingPeriod] = useState<string | null>(null);
  const [periodReasonModal, setPeriodReasonModal] = useState<{ period: string; targetStatus: 'open' | 'locked' } | null>(null);
  const [modalReason, setModalReason] = useState('');

  // Load Period Locks
  const loadPeriods = async () => {
    setLoadingPeriods(true);
    try {
      const data = await apiRequest<{ periods: PeriodLock[] }>('/api/invoices/periods');
      if (data) {
        setPeriods(data.periods || []);
      }
    } catch {
      showToast('Failed to load financial period locks', 'error');
    } finally {
      setLoadingPeriods(false);
    }
  };

  // Toggle Period Lock
  const handleToggleLock = async (period: string, targetStatus: 'open' | 'locked', reason?: string) => {
    setTogglingPeriod(period);
    try {
      const data = await apiRequest<any>(`/api/invoices/periods/${period}/toggle`, {
        method: 'POST',
        body: JSON.stringify({
          status: targetStatus,
          reason: reason || (targetStatus === 'open' ? 'Manually unlocked by Admin' : 'Manually locked by Admin'),
        }),
      });
      if (data) {
        showToast(data.message || `Period ${period} updated.`, 'success');
        loadPeriods();
        setPeriodReasonModal(null);
        setModalReason('');
      }
    } catch (err: any) {
      showToast(err.message || 'Failed to toggle period lock', 'error');
    } finally {
      setTogglingPeriod(null);
    }
  };

  // Import State
  const [csvContent, setCsvContent] = useState('');
  const [fileName, setFileName] = useState('');
  const [previewData, setPreviewData] = useState<any | null>(null);
  const [manualMappings, setManualMappings] = useState<Record<string, number>>({});
  const [isImporting, setIsImporting] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load Analytics
  const loadAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (salesPersonFilter) params.set('salesPerson', salesPersonFilter);
      if (includeCancelledInDashboard) params.set('includeCancelled', 'true');

      const data = await apiRequest<InvoiceAnalytics>(`/api/invoices/analytics/dashboard?${params.toString()}`);
      if (data) {
        setAnalytics(data);
      }
    } catch {
      showToast('Failed to load profitability analytics', 'error');
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // Load Invoices
  const loadInvoices = async () => {
    setLoadingInvoices(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });
      if (searchQuery) params.set('search', searchQuery);
      if (salesPersonFilter) params.set('salesPerson', salesPersonFilter);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (marginFilter) params.set('margin', marginFilter);
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);

      const data = await apiRequest<{ items: SalesInvoice[]; pagination: { total: number } }>(
        `/api/invoices?${params.toString()}`
      );
      if (data) {
        setInvoices(data.items || []);
        setInvoicesTotal(data.pagination?.total || 0);
      }
    } catch {
      showToast('Failed to load sales invoices', 'error');
    } finally {
      setLoadingInvoices(false);
    }
  };

  // Load Aliases
  const loadAliases = async () => {
    setLoadingAliases(true);
    try {
      const data = await apiRequest<{ items: ProductAlias[] }>('/api/invoices/aliases');
      if (data) {
        setAliases(data.items || []);
      }
    } catch {
      showToast('Failed to load product aliases', 'error');
    } finally {
      setLoadingAliases(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, [startDate, endDate, salesPersonFilter, includeCancelledInDashboard]);

  useEffect(() => {
    if (activeTab === 'invoices') {
      loadInvoices();
    } else if (activeTab === 'aliases') {
      loadAliases();
    } else if (activeTab === 'periods') {
      loadPeriods();
    }
  }, [activeTab, page, searchQuery, salesPersonFilter, startDate, endDate, marginFilter, statusFilter]);

  // Invoice Details
  const openInvoiceDetails = async (inv: SalesInvoice) => {
    setSelectedInvoice(inv);
    setLoadingDetail(true);
    try {
      const data = await apiRequest<{ invoice: SalesInvoice; items: SalesInvoiceItem[] }>(`/api/invoices/${inv.id}`);
      if (data) {
        setSelectedItems(data.items || []);
      }
    } catch {
      showToast('Failed to load invoice items', 'error');
    } finally {
      setLoadingDetail(false);
    }
  };

  // Handle File Upload for Import
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = async (event) => {
      const content = event.target?.result as string;
      setCsvContent(content);
      await triggerPreview(content, {});
    };
    reader.readAsText(file);
  };

  // Trigger Preview
  const triggerPreview = async (content: string, mappings: Record<string, number>) => {
    setIsPreviewing(true);
    try {
      const data = await apiRequest<any>('/api/invoices/preview', {
        method: 'POST',
        body: JSON.stringify({
          csvContent: content,
          manualMappings: mappings,
        }),
      });
      if (data) {
        setPreviewData(data);
      }
    } catch (err: any) {
      showToast(err.message || 'Error parsing invoice preview', 'error');
    } finally {
      setIsPreviewing(false);
    }
  };

  // Map an unmatched item in preview
  const handleMapItem = (unmatchedName: string, productId: number) => {
    const updated = { ...manualMappings, [unmatchedName]: productId };
    setManualMappings(updated);
    triggerPreview(csvContent, updated);
  };

  // Commit Invoices Import
  const commitImport = async () => {
    if (!previewData || !previewData.invoices) return;
    setIsImporting(true);

    try {
      // Build alias list to remember
      const aliasesList = Object.entries(manualMappings).map(([name, prodId]) => ({
        alias_name: name,
        product_id: prodId,
      }));

      const data = await apiRequest<any>('/api/invoices/import', {
        method: 'POST',
        body: JSON.stringify({
          invoices: previewData.invoices,
          aliases: aliasesList,
          admin_override: adminOverride,
          override_reason: overrideReason.trim() || undefined,
        }),
      });

      if (data) {
        showToast(data.message || 'Invoices imported successfully!', 'success');
        setCsvContent('');
        setFileName('');
        setPreviewData(null);
        setManualMappings({});
        setAdminOverride(false);
        setOverrideReason('');
        setActiveTab('dashboard');
        loadAnalytics();
      }
    } catch (err: any) {
      showToast(err.message || 'Error importing invoices', 'error');
    } finally {
      setIsImporting(false);
    }
  };

  // Format currency
  const formatAED = (val?: number) => {
    if (val === undefined || val === null) return '—';
    return `AED ${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // GP Badge Component
  const renderGpBadge = (margin?: number) => {
    if (margin === undefined || margin === null) return null;
    let color = 'bg-amber-100 text-amber-800 border-amber-300';
    if (margin >= 25) color = 'bg-emerald-100 text-emerald-800 border-emerald-300';
    else if (margin < 10) color = 'bg-rose-100 text-rose-800 border-rose-300';

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${color}`}>
        {margin > 0 ? `+${margin.toFixed(1)}%` : `${margin.toFixed(1)}%`}
      </span>
    );
  };

  // Status Badge Component
  const renderStatusBadge = (status?: string) => {
    const s = (status || '').toLowerCase();
    if (s.includes('cancel')) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-rose-100 text-rose-700 border border-rose-200">
          Cancelled
        </span>
      );
    }
    if (s.includes('paid')) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
          Paid
        </span>
      );
    }
    if (s.includes('overdue')) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-200">
          Overdue
        </span>
      );
    }
    if (s.includes('return')) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-orange-100 text-orange-700 border border-orange-200">
          Return
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unpaid'}
      </span>
    );
  };

  const kpis = analytics?.kpis;
  const currentCost = costMode === 'net_cost' ? kpis?.total_base_cost : kpis?.total_raw_cost;
  const currentGp = kpis && currentCost !== undefined ? kpis.total_sold_amount - currentCost : undefined;
  const currentGpPct = kpis && currentGp !== undefined && kpis.total_sold_amount > 0 ? (currentGp / kpis.total_sold_amount) * 100 : undefined;

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-emerald-50 rounded-lg text-emerald-600">
              <TrendingUp className="w-6 h-6" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Sales & GP Profitability Tracker</h1>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            ERPNext Sales Invoices, automated product valuation, and Gross Profit (GP) performance.
          </p>
        </div>

        {/* Global Controls */}
        <div className="flex flex-wrap items-center gap-3">
          {canViewCost && (
            <div className="flex items-center bg-slate-100 p-1 rounded-lg border border-slate-200 text-xs">
              <button
                type="button"
                onClick={() => setCostMode('net_cost')}
                className={`px-3 py-1.5 rounded-md font-medium transition ${
                  costMode === 'net_cost' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Net Cost (Landing)
              </button>
              <button
                type="button"
                onClick={() => setCostMode('raw_cost')}
                className={`px-3 py-1.5 rounded-md font-medium transition ${
                  costMode === 'raw_cost' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Raw Cost
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={() => setActiveTab('import')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg text-sm font-medium shadow-sm transition"
          >
            <Upload className="w-4 h-4" />
            Import ERP Invoices
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex border-b border-slate-200 bg-white px-4 rounded-t-xl overflow-x-auto">
        <button
          type="button"
          onClick={() => setActiveTab('dashboard')}
          className={`py-3.5 px-4 text-sm font-medium border-b-2 transition flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'dashboard'
              ? 'border-emerald-600 text-emerald-700'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <TrendingUp className="w-4 h-4" />
          GP Analytics Dashboard
        </button>
        <button
          type="button"
          onClick={() => navigate('/invoices')}
          className="py-3.5 px-4 text-sm font-medium border-b-2 border-transparent text-slate-500 hover:text-emerald-700 transition flex items-center gap-2 whitespace-nowrap group"
        >
          <FileSpreadsheet className="w-4 h-4 text-slate-400 group-hover:text-emerald-600" />
          <span>Invoices Ledger</span>
          <span className="px-1.5 py-0.5 rounded text-[10px] bg-slate-100 text-slate-600 font-bold group-hover:bg-emerald-100 group-hover:text-emerald-800">
            Dedicated Page
          </span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('import')}
          className={`py-3.5 px-4 text-sm font-medium border-b-2 transition flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'import'
              ? 'border-emerald-600 text-emerald-700'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Upload className="w-4 h-4" />
          ERP Data Importer
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('aliases')}
          className={`py-3.5 px-4 text-sm font-medium border-b-2 transition flex items-center gap-2 ${
            activeTab === 'aliases'
              ? 'border-emerald-600 text-emerald-700'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Layers className="w-4 h-4" />
          Product Mappings ({aliases.length})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('periods')}
          className={`py-3.5 px-4 text-sm font-medium border-b-2 transition flex items-center gap-2 ${
            activeTab === 'periods'
              ? 'border-emerald-600 text-emerald-700'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Lock className="w-4 h-4" />
          Period Locks
        </button>
      </div>

      {/* TAB 1: GP ANALYTICS DASHBOARD */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          {/* Filters Bar */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center gap-2 text-slate-600 font-medium">
              <Calendar className="w-4 h-4 text-slate-400" />
              <span>Date Range:</span>
            </div>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <span className="text-slate-400">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />

            {analytics?.filter_options.sales_persons && analytics.filter_options.sales_persons.length > 0 && (
              <div className="flex items-center gap-2 ml-auto">
                <User className="w-4 h-4 text-slate-400" />
                <select
                  value={salesPersonFilter}
                  onChange={(e) => setSalesPersonFilter(e.target.value)}
                  className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">All Sales Representatives</option>
                  {analytics.filter_options.sales_persons.map((sp) => (
                    <option key={sp} value={sp}>
                      {sp}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {(startDate || endDate || salesPersonFilter) && (
              <button
                type="button"
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                  setSalesPersonFilter('');
                }}
                className="text-xs text-rose-600 hover:underline ml-2"
              >
                Clear Filters
              </button>
            )}
          </div>

          {/* Cancelled Invoices Notification Banner */}
          {kpis?.cancelled_count && kpis.cancelled_count > 0 ? (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3 text-xs text-rose-800">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 bg-rose-100 rounded-lg text-rose-700">
                  <AlertTriangle className="w-4 h-4" />
                </div>
                <span>
                  <strong>{kpis.cancelled_count} Cancelled invoices</strong> totaling <strong>{formatAED(kpis.cancelled_amount)}</strong> are excluded from your Gross Profit and Revenue KPIs.
                </span>
              </div>
              <button
                type="button"
                onClick={() => setIncludeCancelledInDashboard(!includeCancelledInDashboard)}
                className="px-3 py-1 bg-white border border-rose-300 rounded-lg font-semibold text-rose-700 hover:bg-rose-100/50 shadow-sm transition"
              >
                {includeCancelledInDashboard ? 'Exclude Cancelled' : 'Include Cancelled for Audit'}
              </button>
            </div>
          ) : null}

          {/* KPI Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Card 1: Invoiced Revenue */}
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Invoiced Revenue</span>
                <span className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                  <DollarSign className="w-5 h-5" />
                </span>
              </div>
              <div className="mt-3">
                <span className="text-2xl font-bold text-slate-900">{formatAED(kpis?.total_sold_amount)}</span>
                <span className="block text-xs text-slate-500 mt-1">{kpis?.total_invoices || 0} Invoices processed</span>
              </div>
            </div>

            {/* Card 2: Total Base Cost */}
            {canViewCost && (
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Total Base Cost ({costMode === 'net_cost' ? 'Net' : 'Raw'})
                  </span>
                  <span className="p-2 bg-slate-100 text-slate-600 rounded-lg">
                    <Layers className="w-5 h-5" />
                  </span>
                </div>
                <div className="mt-3">
                  <span className="text-2xl font-bold text-slate-900">{formatAED(currentCost)}</span>
                  <span className="block text-xs text-slate-500 mt-1">Catalog procurement base</span>
                </div>
              </div>
            )}

            {/* Card 3: Total Gross Profit (AED) */}
            {canViewCost && (
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Gross Profit (GP)</span>
                  <span className="p-2 bg-emerald-50 text-emerald-600 rounded-lg">
                    <TrendingUp className="w-5 h-5" />
                  </span>
                </div>
                <div className="mt-3">
                  <span className="text-2xl font-bold text-emerald-700">{formatAED(currentGp)}</span>
                  <span className="block text-xs text-slate-500 mt-1">Total commercial spread</span>
                </div>
              </div>
            )}

            {/* Card 4: Overall GP Margin % */}
            {canViewCost && (
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Overall GP Margin</span>
                  {renderGpBadge(currentGpPct)}
                </div>
                <div className="mt-3">
                  <span className="text-2xl font-bold text-slate-900">
                    {currentGpPct !== undefined ? `${currentGpPct.toFixed(1)}%` : '—'}
                  </span>
                  <span className="block text-xs text-slate-500 mt-1">Weighted average profitability</span>
                </div>
              </div>
            )}
          </div>

          {/* Grid: Sales Rep Leaderboard & Top Customers */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sales Rep Leaderboard */}
            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-slate-900 flex items-center gap-2">
                  <User className="w-4 h-4 text-emerald-600" />
                  Sales Representative Performance
                </h3>
                <span className="text-xs text-slate-400">{analytics?.sales_reps.length || 0} Reps</span>
              </div>

              {analytics?.sales_reps && analytics.sales_reps.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {analytics.sales_reps.map((rep, idx) => (
                    <div key={rep.sales_person} className="py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-600 text-xs font-bold flex items-center justify-center">
                          {idx + 1}
                        </span>
                        <div>
                          <div className="font-semibold text-slate-800 text-sm">{rep.sales_person}</div>
                          <div className="text-xs text-slate-400">{rep.invoice_count} invoices</div>
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="font-bold text-slate-900 text-sm">{formatAED(rep.total_sold)}</div>
                        {canViewCost && (
                          <div className="flex items-center justify-end gap-2 mt-0.5">
                            <span className="text-xs text-slate-500">GP: {formatAED(rep.total_gp)}</span>
                            {renderGpBadge(rep.gp_margin_pct)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-8">No invoice data available for this range.</p>
              )}
            </div>

            {/* Top Customer Accounts */}
            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-slate-900 flex items-center gap-2">
                  <Building className="w-4 h-4 text-blue-600" />
                  Top Customer Accounts
                </h3>
                <span className="text-xs text-slate-400">By Billed Revenue</span>
              </div>

              {analytics?.top_customers && analytics.top_customers.length > 0 ? (
                <div className="divide-y divide-slate-100 max-h-[380px] overflow-y-auto pr-1">
                  {analytics.top_customers.map((c) => (
                    <div key={c.customer_name} className="py-3 flex items-center justify-between">
                      <div className="max-w-[65%]">
                        <div className="font-medium text-slate-800 text-sm truncate" title={c.customer_name}>
                          {c.customer_name}
                        </div>
                        <div className="text-xs text-slate-400">{c.invoice_count} orders</div>
                      </div>

                      <div className="text-right">
                        <div className="font-bold text-slate-900 text-sm">{formatAED(c.total_sold)}</div>
                        {canViewCost && (
                          <div className="flex items-center justify-end gap-2 mt-0.5">
                            {renderGpBadge(c.gp_margin_pct)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-8">No customer records found.</p>
              )}
            </div>
          </div>

          {/* Monthly Sales & Profitability Timeline */}
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
              <div>
                <h3 className="font-bold text-slate-900 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-emerald-600" />
                  Monthly Performance Timeline
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Historical monthly trend of invoices captured from ERP, billed revenue, and gross profit
                </p>
              </div>
              <span className="text-xs text-slate-400 font-mono">
                {analytics?.monthly_trend?.length || 0} financial periods
              </span>
            </div>

            {analytics?.monthly_trend && analytics.monthly_trend.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-500 text-xs uppercase bg-slate-50">
                      <th className="py-3 px-4">Period (Month)</th>
                      <th className="py-3 px-4 text-center">Invoices Captured</th>
                      <th className="py-3 px-4 text-right">Total Billed</th>
                      {canViewCost && <th className="py-3 px-4 text-right">Base Cost</th>}
                      {canViewCost && <th className="py-3 px-4 text-right">Gross Profit</th>}
                      {canViewCost && <th className="py-3 px-4 text-right">GP Margin</th>}
                      <th className="py-3 px-4 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {analytics.monthly_trend.map((m) => (
                      <tr key={m.month} className="hover:bg-slate-50/80 transition group">
                        <td className="py-3.5 px-4 font-bold text-slate-900 font-mono text-xs">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 rounded-md border border-slate-200">
                            <Calendar className="w-3.5 h-3.5 text-slate-500" />
                            {m.month}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 text-center font-medium text-slate-700">
                          <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-800 font-semibold">
                            {m.invoice_count} invoices
                          </span>
                        </td>
                        <td className="py-3.5 px-4 text-right font-bold text-slate-900 font-mono">
                          {formatAED(m.total_sold)}
                        </td>
                        {canViewCost && (
                          <td className="py-3.5 px-4 text-right text-slate-600 font-mono text-xs">
                            {formatAED(m.total_base_cost)}
                          </td>
                        )}
                        {canViewCost && (
                          <td className="py-3.5 px-4 text-right font-bold text-emerald-700 font-mono">
                            {formatAED(m.total_gp)}
                          </td>
                        )}
                        {canViewCost && (
                          <td className="py-3.5 px-4 text-right">
                            {renderGpBadge(m.gp_margin_pct)}
                          </td>
                        )}
                        <td className="py-3.5 px-4 text-center">
                          <button
                            type="button"
                            onClick={() => {
                              navigate(`/invoices?period=${m.month}`);
                            }}
                            className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition"
                          >
                            <span>View Invoices</span>
                            <ChevronRight className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-8">No monthly timeline data available.</p>
            )}
          </div>

          {/* Product Profitability Matrix */}
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-900 flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                Product Profitability Matrix (Top Products Sold)
              </h3>
              <span className="text-xs text-slate-400">Sorted by revenue</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-500 text-xs uppercase bg-slate-50">
                    <th className="py-3 px-4">Product Name</th>
                    <th className="py-3 px-4">Category</th>
                    <th className="py-3 px-4 text-right">Units Sold</th>
                    <th className="py-3 px-4 text-right">Total Billed</th>
                    {canViewCost && <th className="py-3 px-4 text-right">Base Cost</th>}
                    {canViewCost && <th className="py-3 px-4 text-right">Gross Profit</th>}
                    {canViewCost && <th className="py-3 px-4 text-right">GP Margin</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {analytics?.products && analytics.products.length > 0 ? (
                    analytics.products.map((p) => (
                      <tr key={p.product_name} className="hover:bg-slate-50 transition">
                        <td className="py-3 px-4 font-medium text-slate-800">{p.product_name}</td>
                        <td className="py-3 px-4 text-xs text-slate-500">{p.category}</td>
                        <td className="py-3 px-4 text-right font-medium">{p.total_qty.toLocaleString()}</td>
                        <td className="py-3 px-4 text-right font-bold text-slate-900">{formatAED(p.total_sold)}</td>
                        {canViewCost && <td className="py-3 px-4 text-right text-slate-600">{formatAED(p.total_base_cost)}</td>}
                        {canViewCost && (
                          <td className="py-3 px-4 text-right font-semibold text-emerald-700">
                            {formatAED(p.total_gp)}
                          </td>
                        )}
                        {canViewCost && <td className="py-3 px-4 text-right">{renderGpBadge(p.gp_margin_pct)}</td>}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center py-8 text-slate-400 text-sm">
                        No product sales recorded yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: INVOICES LEDGER */}
      {activeTab === 'invoices' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Controls Bar */}
          <div className="p-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-[280px]">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                <input
                  type="text"
                  placeholder="Search by Invoice #, Customer, or Salesperson..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(1);
                  }}
                  className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="all">All Invoices</option>
                <option value="active">Active Only (Excl. Cancelled)</option>
                <option value="unpaid">Unpaid</option>
                <option value="paid">Paid</option>
                <option value="cancelled">Cancelled</option>
                <option value="overdue">Overdue</option>
              </select>

              {canViewCost && (
                <select
                  value={marginFilter}
                  onChange={(e) => {
                    setMarginFilter(e.target.value);
                    setPage(1);
                  }}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">All Margins</option>
                  <option value="healthy">Healthy Margin (≥ 20%)</option>
                  <option value="low">Low Margin (&lt; 20%)</option>
                  <option value="negative">Negative Margin (&lt; 0%)</option>
                </select>
              )}
            </div>

            <div className="text-xs text-slate-500">
              Total Invoices: <span className="font-bold text-slate-800">{invoicesTotal}</span>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-slate-500 text-xs uppercase bg-slate-50">
                  <th className="py-3 px-4">Invoice #</th>
                  <th className="py-3 px-4">Date</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Customer Name</th>
                  <th className="py-3 px-4">Sales Person</th>
                  <th className="py-3 px-4 text-right">Total Billed</th>
                  {canViewCost && <th className="py-3 px-4 text-right">Base Cost</th>}
                  {canViewCost && <th className="py-3 px-4 text-right">GP Amount</th>}
                  {canViewCost && <th className="py-3 px-4 text-right">GP %</th>}
                  <th className="py-3 px-4 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loadingInvoices ? (
                  <tr>
                    <td colSpan={10} className="text-center py-12 text-slate-400">
                      Loading invoices...
                    </td>
                  </tr>
                ) : invoices.length > 0 ? (
                  invoices.map((inv) => (
                    <tr key={inv.id} className={`hover:bg-slate-50 transition ${inv.status === 'cancelled' ? 'bg-rose-50/20' : ''}`}>
                      <td className="py-3 px-4 font-mono font-bold text-emerald-800">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span>{inv.invoice_no}</span>
                          {inv.is_locked && (
                            <span title={`Period ${inv.period || ''} is locked`} className="inline-flex items-center text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded text-[10px] font-sans font-medium">
                              <Lock className="w-2.5 h-2.5 mr-0.5" />
                              Locked
                            </span>
                          )}
                          {Boolean(inv.has_active_negative_stock) && (
                            <span
                              title="Invoice generated with negative physical stock. Factory production batch required to clear."
                              className="inline-flex items-center gap-1 text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded-full text-[10px] font-sans font-bold whitespace-nowrap animate-pulse"
                            >
                              <AlertTriangle className="w-2.5 h-2.5 text-rose-600" />
                              Negative Stock
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-600">{inv.invoice_date}</td>
                      <td className="py-3 px-4">{renderStatusBadge(inv.status)}</td>
                      <td className="py-3 px-4 font-medium text-slate-800 max-w-[220px] truncate" title={inv.customer_name}>
                        {inv.customer_name}
                      </td>
                      <td className="py-3 px-4 text-slate-600">{inv.sales_person}</td>
                      <td className={`py-3 px-4 text-right font-bold ${inv.status === 'cancelled' ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
                        {formatAED(inv.total_sold_amount)}
                      </td>
                      {canViewCost && (
                        <td className={`py-3 px-4 text-right ${inv.status === 'cancelled' ? 'text-slate-400 line-through' : 'text-slate-600'}`}>
                          {formatAED(costMode === 'net_cost' ? inv.total_base_cost : inv.total_raw_cost)}
                        </td>
                      )}
                      {canViewCost && (
                        <td className={`py-3 px-4 text-right font-semibold ${inv.status === 'cancelled' ? 'text-slate-400 line-through' : 'text-emerald-700'}`}>
                          {formatAED(inv.total_gp_amount)}
                        </td>
                      )}
                      {canViewCost && (
                        <td className="py-3 px-4 text-right">
                          {inv.status === 'cancelled' ? (
                            <span className="text-xs text-rose-500 font-medium">Voided</span>
                          ) : (
                            renderGpBadge(inv.gp_margin_pct)
                          )}
                        </td>
                      )}
                      <td className="py-3 px-4 text-center">
                        <button
                          type="button"
                          onClick={() => openInvoiceDetails(inv)}
                          className="p-1.5 hover:bg-slate-200 rounded text-slate-600 hover:text-slate-900 transition"
                          title="View line items & GP breakdown"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="text-center py-12 text-slate-400">
                      No invoices found matching your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 3: ERP DATA IMPORTER */}
      {activeTab === 'import' && (
        <div className="space-y-6">
          {/* Instructions Banner */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 flex items-start gap-4">
            <div className="p-2 bg-emerald-100 rounded-lg text-emerald-700 mt-0.5">
              <FileSpreadsheet className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-bold text-emerald-900">Tally & ERPNext Sales Invoice Bulk Importer</h3>
              <p className="text-sm text-emerald-800 mt-1 leading-relaxed">
                Export your sales vouchers from <strong>Tally</strong> (via <em>Display &gt; Account Books &gt; Sales Register &gt; Columnar Export</em> as CSV) or <strong>ERPNext</strong> and upload here.
                The system automatically detects native Tally columns (<em>Vch No, Date, Particulars/Party's A/c, Item Name, Quantity, Rate, Amount</em>), normalizes dates, matches items to your 59 catalog products, computes base procurement costs, and calculates Gross Profit margins in real-time.
              </p>
            </div>
          </div>

          {/* Drag & Drop Upload Zone */}
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-slate-300 hover:border-emerald-500 bg-white hover:bg-emerald-50/20 p-12 rounded-xl flex flex-col items-center justify-center cursor-pointer transition text-center shadow-sm"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="hidden"
            />
            <div className="p-4 bg-emerald-50 text-emerald-700 rounded-full mb-3">
              <Upload className="w-8 h-8" />
            </div>
            <h4 className="font-bold text-slate-800 text-lg">Click or drag & drop Tally or ERPNext CSV export</h4>
            <p className="text-xs text-slate-500 mt-1">Supports native Tally Columnar Sales Register and ERPNext Sales Invoice formats</p>
            {fileName && (
              <div className="mt-3 px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs font-semibold">
                Selected: {fileName}
              </div>
            )}
          </div>

          {/* Validation & Matching Preview */}
          {isPreviewing && (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <RefreshCw className="w-6 h-6 animate-spin text-emerald-600 mx-auto" />
              <p className="text-sm text-slate-500 mt-2 font-medium">Matching ERP line items with catalog...</p>
            </div>
          )}

          {previewData && !isPreviewing && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden p-6 space-y-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between pb-4 border-b border-slate-200 gap-4">
                <div>
                  <h3 className="font-bold text-slate-900 text-lg">Import Preview & Matching Summary</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Found {previewData.summary.total_invoices} invoices ({previewData.summary.total_line_items} items) •{' '}
                    <strong className="text-slate-700">{previewData.summary.active_invoices_count ?? previewData.summary.total_invoices} Active</strong>
                    {previewData.summary.cancelled_invoices_count > 0 && (
                      <span className="text-rose-600 font-semibold"> • {previewData.summary.cancelled_invoices_count} Cancelled</span>
                    )}
                  </p>
                </div>

                {/* Validation Badges */}
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-xs font-semibold">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    {previewData.summary.matched_items_count} Items Auto-Matched
                  </span>

                  {previewData.summary.unmatched_items_count > 0 ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-xs font-semibold">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      {previewData.summary.unmatched_items_count} Unmatched Names
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-xs font-semibold">
                      100% Items Matched
                    </span>
                  )}

                  <button
                    type="button"
                    onClick={commitImport}
                    disabled={isImporting || (previewData.summary.has_locked_periods && !adminOverride)}
                    className="px-5 py-2 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm font-bold shadow transition flex items-center gap-2"
                  >
                    {isImporting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    {previewData.summary.has_locked_periods && !adminOverride
                      ? 'Locked Period (Override Required)'
                      : 'Confirm & Ingest Invoices'}
                  </button>
                </div>
              </div>

              {/* Incremental Change Detection Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">New Invoices</div>
                  <div className="text-xl font-bold text-emerald-700 mt-0.5">
                    {previewData.summary.new_invoices_count ?? 0}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">Will be newly recorded</div>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Updated / Modified</div>
                  <div className="text-xl font-bold text-amber-700 mt-0.5">
                    {previewData.summary.modified_invoices_count ?? 0}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">Existing rows with edits</div>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Unchanged</div>
                  <div className="text-xl font-bold text-slate-700 mt-0.5">
                    {previewData.summary.unchanged_invoices_count ?? 0}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">Identical in database</div>
                </div>

                <div className={`rounded-lg p-3 border ${
                  previewData.summary.locked_invoices_count > 0
                    ? 'bg-amber-50 border-amber-300'
                    : 'bg-slate-50 border-slate-200'
                }`}>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1">
                    <Lock className="w-3 h-3 text-amber-600" />
                    Locked Records
                  </div>
                  <div className={`text-xl font-bold mt-0.5 ${
                    previewData.summary.locked_invoices_count > 0 ? 'text-amber-800' : 'text-slate-700'
                  }`}>
                    {previewData.summary.locked_invoices_count ?? 0}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {previewData.summary.locked_invoices_count > 0 ? 'Closed financial month(s)' : 'All periods active'}
                  </div>
                </div>
              </div>

              {/* Locked Financial Period Alert & Admin Override Box */}
              {previewData.summary.has_locked_periods && (
                <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex flex-col md:flex-row items-start gap-4">
                  <div className="p-2.5 bg-amber-100 rounded-lg text-amber-700 flex-shrink-0">
                    <Lock className="w-6 h-6" />
                  </div>
                  <div className="flex-1 space-y-2">
                    <div>
                      <h4 className="font-bold text-amber-950 text-sm flex items-center gap-2">
                        <span>Financial Period Locked:</span>
                        <span className="font-mono bg-amber-200 text-amber-900 px-2 py-0.5 rounded text-xs">
                          {previewData.summary.locked_periods?.join(', ')}
                        </span>
                      </h4>
                      <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                        This file contains invoices for month(s) that are completed and closed. To prevent accidental alterations to finalized financials, changes to locked months are blocked by default.
                      </p>
                    </div>

                    {user?.role?.isSystemRole ? (
                      <div className="pt-2 border-t border-amber-200 space-y-2">
                        <label className="flex items-center gap-2.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={adminOverride}
                            onChange={(e) => setAdminOverride(e.target.checked)}
                            className="rounded border-amber-400 text-amber-600 focus:ring-amber-500 w-4 h-4 cursor-pointer"
                          />
                          <span className="text-xs font-bold text-amber-950">
                            Super Admin Override: I authorize modifying/inserting records in locked financial period(s)
                          </span>
                        </label>
                        {adminOverride && (
                          <input
                            type="text"
                            placeholder="Reason for overriding locked period (e.g. Audit correction, Year-end reconciliation)..."
                            value={overrideReason}
                            onChange={(e) => setOverrideReason(e.target.value)}
                            className="w-full text-xs p-2 bg-white border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                          />
                        )}
                      </div>
                    ) : (
                      <div className="text-xs font-medium text-rose-700 bg-rose-50 border border-rose-200 p-2.5 rounded-lg flex items-center gap-2">
                        <ShieldAlert className="w-4 h-4 flex-shrink-0 text-rose-600" />
                        <span>Only a Super Administrator can authorize changes to locked periods. Please ask an Administrator to unlock the period first.</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Cancelled Invoices Notification */}
              {previewData.summary.cancelled_invoices_count > 0 && (
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-center gap-3">
                  <div className="p-2 bg-rose-100 rounded-lg text-rose-700 flex-shrink-0">
                    <AlertTriangle className="w-5 h-5" />
                  </div>
                  <div className="text-xs text-rose-900 leading-relaxed">
                    <strong className="text-rose-800 text-sm block font-bold mb-0.5">
                      {previewData.summary.cancelled_invoices_count} Cancelled Invoices Detected ({formatAED(previewData.summary.cancelled_sold_amount)})
                    </strong>
                    These invoices will be ingested with status <strong>Cancelled</strong> and excluded from your active Gross Profit and sales totals to protect reporting accuracy.
                  </div>
                </div>
              )}

              {/* Unmatched Item Mapping Resolver */}
              {previewData.summary.unmatched_item_names.length > 0 && (
                <div className="bg-amber-50/70 border border-amber-200 rounded-xl p-5 space-y-3">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <h4 className="font-bold text-amber-900 text-sm">
                      Map Unmatched ERP Item Names to Catalog Products
                    </h4>
                  </div>
                  <p className="text-xs text-amber-800 leading-relaxed">
                    Some items in your ERP export use alternate descriptions. Select the matching catalog product once below — the system will remember it forever!
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                    {previewData.summary.unmatched_item_names.map((unmatched: string) => (
                      <div key={unmatched} className="bg-white p-3 rounded-lg border border-amber-200 flex flex-col gap-1.5">
                        <span className="text-xs font-mono font-semibold text-slate-800 truncate" title={unmatched}>
                          ERP: {unmatched}
                        </span>
                        <select
                          value={manualMappings[unmatched] || ''}
                          onChange={(e) => handleMapItem(unmatched, parseInt(e.target.value, 10))}
                          className="w-full text-xs p-1.5 border border-slate-300 rounded focus:ring-1 focus:ring-emerald-500"
                        >
                          <option value="">-- Select Matching Product --</option>
                          {previewData.catalog_products.map((p: any) => (
                            <option key={p.id} value={p.id}>
                              #{p.item_no} - {p.name} ({p.category})
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sample Invoices Preview Table */}
              <div className="space-y-3">
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Preview Invoices Data</h4>
                <div className="overflow-x-auto border border-slate-200 rounded-lg">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-200">
                      <tr>
                        <th className="p-3">Invoice #</th>
                        <th className="p-3">Date</th>
                        <th className="p-3">Status</th>
                        <th className="p-3">Customer</th>
                        <th className="p-3">Sales Person</th>
                        <th className="p-3">Item Breakdown</th>
                        <th className="p-3 text-right">Billed Amount</th>
                        <th className="p-3 text-right">Base Cost</th>
                        <th className="p-3 text-right">GP Margin</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {previewData.invoices.slice(0, 10).map((inv: any) => (
                        <tr key={inv.invoice_no} className={`hover:bg-slate-50 ${inv.status === 'cancelled' ? 'bg-rose-50/20' : ''}`}>
                          <td className="p-3 font-mono font-bold text-emerald-800">{inv.invoice_no}</td>
                          <td className="p-3 text-slate-600">{inv.invoice_date}</td>
                          <td className="p-3">{renderStatusBadge(inv.status)}</td>
                          <td className="p-3 font-medium text-slate-800 truncate max-w-[180px]">{inv.customer_name}</td>
                          <td className="p-3 text-slate-600">{inv.sales_person}</td>
                          <td className="p-3 text-slate-600">{inv.items.length} items</td>
                          <td className={`p-3 text-right font-bold ${inv.status === 'cancelled' ? 'text-slate-400 line-through' : 'text-slate-900'}`}>{formatAED(inv.total_sold_amount)}</td>
                          <td className={`p-3 text-right ${inv.status === 'cancelled' ? 'text-slate-400 line-through' : 'text-slate-600'}`}>{formatAED(inv.total_base_cost)}</td>
                          <td className="p-3 text-right">
                            {inv.status === 'cancelled' ? (
                              <span className="text-xs text-rose-500 font-medium">Voided</span>
                            ) : (
                              renderGpBadge(inv.gp_margin_pct)
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 4: PRODUCT MAPPINGS (ALIASES MEMORY) */}
      {activeTab === 'aliases' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
          <div className="flex items-center justify-between pb-4 border-b border-slate-200">
            <div>
              <h3 className="font-bold text-slate-900 text-lg">Product Aliases & Learned ERP Mappings</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                These name mappings are remembered automatically. Any invoice with these ERP names will instantly link to your master catalog.
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-slate-500 text-xs uppercase bg-slate-50">
                  <th className="py-3 px-4">ERP Item Description</th>
                  <th className="py-3 px-4">Matched Geobit Catalog Product</th>
                  <th className="py-3 px-4">Category</th>
                  <th className="py-3 px-4 text-right">Base Net Cost</th>
                  <th className="py-3 px-4 text-right">Client Price</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {aliases.length > 0 ? (
                  aliases.map((a) => (
                    <tr key={a.id} className="hover:bg-slate-50">
                      <td className="py-3 px-4 font-mono font-medium text-slate-800">{a.alias_name}</td>
                      <td className="py-3 px-4 font-bold text-emerald-800 flex items-center gap-1.5">
                        <ChevronRight className="w-3.5 h-3.5 text-emerald-600" />
                        {a.product_name}
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-500">{a.category}</td>
                      <td className="py-3 px-4 text-right text-slate-600">{formatAED(a.net_cost)}</td>
                      <td className="py-3 px-4 text-right font-medium text-slate-900">{formatAED(a.client_price)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center py-10 text-slate-400">
                      No aliases created yet. They will appear here automatically as you map ERP imports.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 5: FINANCIAL PERIOD LOCKS */}
      {activeTab === 'periods' && (
        <div className="space-y-6">
          {/* Header Info */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-50 rounded-xl text-amber-700 mt-1">
                <Lock className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Financial Period Locking (Book Closing)</h3>
                <p className="text-xs text-slate-500 mt-1 max-w-2xl leading-relaxed">
                  Past completed calendar months are auto-locked by default to prevent retroactive alteration or accidental re-importing of closed financial records.
                  Current and future months accept ongoing incremental uploads. Super Administrators have exclusive authority to open or lock specific months.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={loadPeriods}
              disabled={loadingPeriods}
              className="px-4 py-2 border border-slate-300 hover:bg-slate-50 rounded-lg text-xs font-semibold text-slate-700 transition flex items-center gap-1.5 self-start md:self-auto"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loadingPeriods ? 'animate-spin' : ''}`} />
              Refresh Status
            </button>
          </div>

          {/* Periods Table */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50/50">
              <div className="flex items-center gap-2">
                <h4 className="font-bold text-slate-800 text-sm">Monthly Periods Ledger</h4>
                <span className="text-xs text-slate-500">({periods.length} months logged)</span>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1.5 text-rose-700 font-semibold">
                  <Lock className="w-3.5 h-3.5" />
                  {periods.filter(p => p.status === 'locked').length} Locked
                </span>
                <span className="text-slate-300">•</span>
                <span className="flex items-center gap-1.5 text-emerald-700 font-semibold">
                  <Unlock className="w-3.5 h-3.5" />
                  {periods.filter(p => p.status === 'open').length} Open
                </span>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-500 text-xs uppercase bg-slate-50">
                    <th className="py-3 px-4">Financial Month</th>
                    <th className="py-3 px-4 text-center">Classification</th>
                    <th className="py-3 px-4 text-center">Status</th>
                    <th className="py-3 px-4 text-right">Invoices Recorded</th>
                    <th className="py-3 px-4 text-right">Billed Total</th>
                    <th className="py-3 px-4">Audit Note / Activity</th>
                    <th className="py-3 px-4 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loadingPeriods ? (
                    <tr>
                      <td colSpan={7} className="text-center py-12 text-slate-400">
                        <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-slate-400" />
                        Loading financial periods...
                      </td>
                    </tr>
                  ) : periods.length > 0 ? (
                    periods.map((p) => {
                      let monthName = p.period;
                      try {
                        monthName = new Date(p.period + '-01').toLocaleDateString(undefined, {
                          month: 'long',
                          year: 'numeric',
                        });
                      } catch {}

                      return (
                        <tr key={p.period} className="hover:bg-slate-50 transition">
                          <td className="py-3.5 px-4">
                            <div className="font-bold text-slate-900">{monthName}</div>
                            <div className="font-mono text-xs text-slate-400">{p.period}</div>
                          </td>
                          <td className="py-3.5 px-4 text-center">
                            {p.is_past_month ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                                Past Month (Closed)
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                                Current Active Month
                              </span>
                            )}
                          </td>
                          <td className="py-3.5 px-4 text-center">
                            {p.status === 'locked' ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200">
                                <Lock className="w-3.5 h-3.5 text-rose-600" />
                                Locked
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                <Unlock className="w-3.5 h-3.5 text-emerald-600" />
                                Open
                              </span>
                            )}
                          </td>
                          <td className="py-3.5 px-4 text-right font-medium text-slate-800">
                            {p.invoice_count}
                          </td>
                          <td className="py-3.5 px-4 text-right font-bold text-slate-900">
                            {formatAED(p.total_billed)}
                          </td>
                          <td className="py-3.5 px-4 text-xs text-slate-500 max-w-xs">
                            {p.reason ? (
                              <div>
                                <span className="font-medium text-slate-700 block">{p.reason}</span>
                                <span className="text-[11px] text-slate-400">
                                  {p.status === 'open' && p.unlocked_by_name ? `By ${p.unlocked_by_name}` : ''}
                                  {p.status === 'locked' && p.locked_by_name ? `By ${p.locked_by_name}` : ''}
                                </span>
                              </div>
                            ) : (
                              <span className="italic text-slate-400">
                                {p.is_past_month ? 'Auto-locked upon month completion' : 'Open for active month uploads'}
                              </span>
                            )}
                          </td>
                          <td className="py-3.5 px-4 text-center">
                            {user?.role?.isSystemRole ? (
                              <button
                                type="button"
                                onClick={() => setPeriodReasonModal({
                                  period: p.period,
                                  targetStatus: p.status === 'locked' ? 'open' : 'locked',
                                })}
                                disabled={togglingPeriod === p.period}
                                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1 mx-auto ${
                                  p.status === 'locked'
                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-300 hover:bg-emerald-100'
                                    : 'bg-rose-50 text-rose-700 border border-rose-300 hover:bg-rose-100'
                                }`}
                              >
                                {togglingPeriod === p.period ? (
                                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                ) : p.status === 'locked' ? (
                                  <>
                                    <Unlock className="w-3.5 h-3.5" />
                                    Unlock Month
                                  </>
                                ) : (
                                  <>
                                    <Lock className="w-3.5 h-3.5" />
                                    Lock Month
                                  </>
                                )}
                              </button>
                            ) : (
                              <span className="text-xs text-slate-400">Admin Only</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center py-10 text-slate-400">
                        No financial periods recorded yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Period Toggle Reason Modal */}
          {periodReasonModal && (
            <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 backdrop-blur-sm">
              <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4 shadow-2xl animate-in fade-in zoom-in-95">
                <div className="flex items-center justify-between pb-3 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    {periodReasonModal.targetStatus === 'open' ? (
                      <div className="p-2 bg-emerald-100 text-emerald-700 rounded-lg">
                        <Unlock className="w-5 h-5" />
                      </div>
                    ) : (
                      <div className="p-2 bg-rose-100 text-rose-700 rounded-lg">
                        <Lock className="w-5 h-5" />
                      </div>
                    )}
                    <h4 className="font-bold text-slate-900 text-base">
                      {periodReasonModal.targetStatus === 'open' ? 'Unlock Financial Period' : 'Lock Financial Period'}
                    </h4>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setPeriodReasonModal(null);
                      setModalReason('');
                    }}
                    className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="text-xs text-slate-600 leading-relaxed">
                  You are about to set financial period <strong className="font-mono text-slate-800">{periodReasonModal.period}</strong> to{' '}
                  <strong className={periodReasonModal.targetStatus === 'open' ? 'text-emerald-700 font-bold' : 'text-rose-700 font-bold'}>
                    {periodReasonModal.targetStatus.toUpperCase()}
                  </strong>.
                  {periodReasonModal.targetStatus === 'open' ? (
                    <p className="mt-1 text-amber-800 bg-amber-50 p-2 rounded border border-amber-200">
                      Unlocking allows invoices in this month to be modified or newly imported from ERP exports.
                    </p>
                  ) : (
                    <p className="mt-1 text-slate-700 bg-slate-50 p-2 rounded border border-slate-200">
                      Locking prevents any further modifications or imports for this period without Super Admin override.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Reason / Justification (Audit Trail)
                  </label>
                  <textarea
                    rows={3}
                    placeholder={
                      periodReasonModal.targetStatus === 'open'
                        ? 'e.g. Audit correction, Late ERP supplier reconciliation...'
                        : 'e.g. Monthly books closing finalized...'
                    }
                    value={modalReason}
                    onChange={(e) => setModalReason(e.target.value)}
                    className="w-full text-xs p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                  />
                </div>

                <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200">
                  <button
                    type="button"
                    onClick={() => {
                      setPeriodReasonModal(null);
                      setModalReason('');
                    }}
                    className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToggleLock(periodReasonModal.period, periodReasonModal.targetStatus, modalReason)}
                    className={`px-4 py-2 text-xs font-bold text-white rounded-lg shadow transition flex items-center gap-1.5 ${
                      periodReasonModal.targetStatus === 'open'
                        ? 'bg-emerald-600 hover:bg-emerald-700'
                        : 'bg-rose-600 hover:bg-rose-700'
                    }`}
                  >
                    {periodReasonModal.targetStatus === 'open' ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                    Confirm & {periodReasonModal.targetStatus === 'open' ? 'Unlock' : 'Lock'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Invoice Detail Modal / Drawer */}
      {selectedInvoice && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-in fade-in zoom-in-95">
            {/* Header */}
            <div className="p-6 bg-slate-900 text-white flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-emerald-400">Invoice Details</span>
                  <span className="text-xs bg-slate-800 text-slate-300 px-2 py-0.5 rounded">
                    {selectedInvoice.invoice_date}
                  </span>
                  {renderStatusBadge(selectedInvoice.status)}
                </div>
                <h3 className="text-xl font-bold font-mono mt-1 text-white">{selectedInvoice.invoice_no}</h3>
                <p className="text-xs text-slate-300 mt-0.5">
                  Customer: <strong>{selectedInvoice.customer_name}</strong> • Sales Rep: <strong>{selectedInvoice.sales_person}</strong>
                </p>
              </div>

              <button
                type="button"
                onClick={() => setSelectedInvoice(null)}
                className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Line Items Content */}
            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              {/* Cancelled Notice */}
              {selectedInvoice.status === 'cancelled' && (
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-3.5 flex items-center gap-3 text-xs text-rose-800">
                  <div className="p-1.5 bg-rose-100 rounded-lg text-rose-700 flex-shrink-0">
                    <AlertTriangle className="w-4 h-4" />
                  </div>
                  <div>
                    <strong className="font-bold">This invoice is Cancelled.</strong> It is kept in system records for audit compliance, but its figures are excluded from company GP and salesperson performance metrics.
                  </div>
                </div>
              )}

              {/* Negative Stock Notice */}
              {Boolean(selectedInvoice.has_active_negative_stock) && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 flex items-center gap-3 text-xs text-amber-900">
                  <div className="p-1.5 bg-amber-100 rounded-lg text-amber-700 flex-shrink-0">
                    <AlertTriangle className="w-4 h-4" />
                  </div>
                  <div>
                    <strong className="font-bold">Billed with Negative Stock:</strong> This invoice was generated when physical inventory was insufficient (stock was 0 or in deficit). Once Kumar Limbu or the factory logs the corresponding production batch, this alert will automatically clear.
                  </div>
                </div>
              )}

              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
                <div>
                  <span className="text-xs text-slate-500 font-semibold block uppercase">Total Billed</span>
                  <span className="text-lg font-bold text-slate-900">{formatAED(selectedInvoice.total_sold_amount)}</span>
                </div>
                {canViewCost && (
                  <div>
                    <span className="text-xs text-slate-500 font-semibold block uppercase">Total Base Cost</span>
                    <span className="text-lg font-bold text-slate-700">{formatAED(selectedInvoice.total_base_cost)}</span>
                  </div>
                )}
                {canViewCost && (
                  <div>
                    <span className="text-xs text-slate-500 font-semibold block uppercase">Gross Profit</span>
                    <span className="text-lg font-bold text-emerald-700">{formatAED(selectedInvoice.total_gp_amount)}</span>
                  </div>
                )}
                {canViewCost && (
                  <div>
                    <span className="text-xs text-slate-500 font-semibold block uppercase">GP Margin</span>
                    <div className="mt-0.5">{renderGpBadge(selectedInvoice.gp_margin_pct)}</div>
                  </div>
                )}
              </div>

              {/* Line Items Table */}
              <div>
                <h4 className="font-bold text-slate-900 text-sm mb-3">Line Items & Valuation Breakdown</h4>
                <div className="overflow-x-auto border border-slate-200 rounded-lg">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-100 text-slate-600 font-semibold">
                      <tr>
                        <th className="p-3">ERP Item Description</th>
                        <th className="p-3">Catalog Matched Product</th>
                        <th className="p-3 text-right">Qty</th>
                        <th className="p-3 text-right">Sold Rate</th>
                        <th className="p-3 text-right">Total Billed</th>
                        {canViewCost && <th className="p-3 text-right">Base Cost</th>}
                        {canViewCost && <th className="p-3 text-right">GP (AED)</th>}
                        {canViewCost && <th className="p-3 text-right">GP %</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {selectedItems.map((item) => (
                        <tr key={item.id} className="hover:bg-slate-50">
                          <td className="p-3 font-medium text-slate-800">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span>{item.item_name}</span>
                              {Boolean(item.had_negative_stock) && item.current_stock_balance !== undefined && item.current_stock_balance < 0 && (
                                <span className="inline-flex items-center gap-1 text-[10px] text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded-full font-bold whitespace-nowrap">
                                  <AlertTriangle className="w-2.5 h-2.5 text-rose-600" />
                                  Deficit ({item.current_stock_balance} units)
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-3 text-emerald-700 font-semibold">
                            {item.catalog_product_name || (
                              <span className="text-amber-600 italic">Unmatched</span>
                            )}
                          </td>
                          <td className="p-3 text-right font-medium">{item.qty} {item.uom}</td>
                          <td className="p-3 text-right">{formatAED(item.sold_rate)}</td>
                          <td className="p-3 text-right font-bold text-slate-900">{formatAED(item.sold_amount)}</td>
                          {canViewCost && <td className="p-3 text-right text-slate-600">{formatAED(item.total_base_cost)}</td>}
                          {canViewCost && (
                            <td className="p-3 text-right font-semibold text-emerald-700">
                              {formatAED(item.gp_amount)}
                            </td>
                          )}
                          {canViewCost && <td className="p-3 text-right">{renderGpBadge(item.gp_margin_pct)}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-end">
              <button
                type="button"
                onClick={() => setSelectedInvoice(null)}
                className="px-5 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-sm font-semibold transition"
              >
                Close Details
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
